import { Component, EventEmitter, Input, Output, computed, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';

import {
  ButtonComponent, FieldComponent, ModalComponent,
  SelectComponent, TypeaheadComponent,
  type SelectOption,
} from '@echospaces-web-monorepo/es-shared-ui';

import type { Content } from '../content/content.service';
import type { PlaceholderUsedAs } from './link.service';
import type { Placeholder, PlaceholderType } from './placeholder.service';

/**
 * Default "used as" code per placeholder type. The user can override it
 * via the select — these are pragmatic defaults that match what most
 * customers want for the typical content kind on each placeholder type.
 */
const DEFAULT_USED_AS: Record<PlaceholderType, PlaceholderUsedAs> = {
  visible: 'image',
  audible: 'sound',
  readable: 'text',
  usable: 'object',
  reactive: 'info',
};

/**
 * Plausible `usedAs` codes per placeholder type — used to filter the
 * select options so a "visible" placeholder doesn't accidentally get a
 * `sound` link. The select still falls back to ALL options if the
 * placeholder type isn't recognized (defensive).
 */
const USED_AS_BY_TYPE: Record<PlaceholderType, PlaceholderUsedAs[]> = {
  visible: ['image', 'slideshow', 'video'],
  audible: ['sound', 'radio'],
  readable: ['text', 'info'],
  usable: ['object'],
  reactive: ['info'],
};

const ALL_USED_AS: SelectOption[] = [
  { value: 'image', label: 'Image' },
  { value: 'slideshow', label: 'Slideshow' },
  { value: 'video', label: 'Video' },
  { value: 'sound', label: 'Sound' },
  { value: 'radio', label: 'Radio' },
  { value: 'object', label: '3D Object' },
  { value: 'text', label: 'Text' },
  { value: 'info', label: 'Info' },
];

/**
 * Modal to link a Content object into a Placeholder. Two inputs:
 *   1. Content picker (typeahead over the island's content library)
 *   2. "Used as" — defaults from the placeholder's type but overridable
 *      so the user can disambiguate e.g. image vs slideshow on a visible
 *      placeholder.
 */
@Component({
  selector: 'es-link-content-modal',
  imports: [
    FormsModule, ModalComponent, ButtonComponent, FieldComponent,
    SelectComponent, TypeaheadComponent,
  ],
  template: `
    <es-modal [open]="open" [title]="modalTitle()" width="md" (closed)="onCancel()">
      <div class="p-6 space-y-4">
        <es-field
          label="Content"
          hint="Search by title in the current island's content library."
          [error]="contentError()"
        >
          <es-typeahead
            [items]="contents"
            [trackByFn]="trackContent"
            [labelFn]="contentLabel"
            [sublabelFn]="contentSublabel"
            placeholder="Search content…"
            noItemsText="No content available — upload some first."
            (itemSelected)="onContentPicked($event)"
          ></es-typeahead>
        </es-field>

        <es-field label="Used as" hint="How the placeholder should present this content.">
          <es-select [options]="usedAsOptions()" [(ngModel)]="usedAs"></es-select>
        </es-field>

        @if (submitError()) {
          <p class="text-sm text-red-400">{{ submitError() }}</p>
        }

        <div class="flex justify-end gap-2 pt-2">
          <es-button variant="ghost" (clicked)="onCancel()">Cancel</es-button>
          <es-button
            variant="primary"
            [disabled]="submitting() || !selectedContent()"
            (clicked)="onSubmit()"
          >
            {{ submitting() ? 'Linking…' : 'Link content' }}
          </es-button>
        </div>
      </div>
    </es-modal>
  `,
})
export class LinkContentModalComponent {
  @Input() open = false;
  @Input() contents: Content[] = [];
  @Input() set placeholder(value: Placeholder | null | undefined) {
    this._placeholder.set(value ?? null);
    if (value) this.usedAs = DEFAULT_USED_AS[value.type];
  }

  @Output() closed = new EventEmitter<void>();
  @Output() submitted = new EventEmitter<{
    contentId: string;
    usedAs: PlaceholderUsedAs;
  }>();

  private readonly _placeholder = signal<Placeholder | null>(null);

  readonly selectedContent = signal<Content | null>(null);
  usedAs: PlaceholderUsedAs = 'image';

  readonly submitting = signal(false);
  readonly contentError = signal<string | null>(null);
  readonly submitError = signal<string | null>(null);

  readonly modalTitle = computed(() => {
    const p = this._placeholder();
    return p ? `Link content to "${p.displayName ?? p.slug}"` : 'Link content';
  });

  readonly usedAsOptions = computed<SelectOption[]>(() => {
    const type = this._placeholder()?.type;
    if (!type) return ALL_USED_AS;
    const allowed = new Set(USED_AS_BY_TYPE[type]);
    const filtered = ALL_USED_AS.filter((o) => allowed.has(o.value as PlaceholderUsedAs));
    return filtered.length > 0 ? filtered : ALL_USED_AS;
  });

  readonly trackContent = (c: Content) => c.id;
  readonly contentLabel = (c: Content) => c.title;
  readonly contentSublabel = (c: Content) => c.description ?? c.type;

  onContentPicked(content: Content): void {
    this.selectedContent.set(content);
    this.contentError.set(null);
  }

  onSubmit(): void {
    const content = this.selectedContent();
    if (!content) {
      this.contentError.set('Pick a content object');
      return;
    }
    this.submitting.set(true);
    this.submitted.emit({ contentId: content.id, usedAs: this.usedAs });
  }

  finishSubmit(error?: string): void {
    this.submitting.set(false);
    this.submitError.set(error ?? null);
    if (!error) {
      this.selectedContent.set(null);
      this.closed.emit();
    }
  }

  onCancel(): void {
    this.selectedContent.set(null);
    this.contentError.set(null);
    this.submitError.set(null);
    this.closed.emit();
  }
}
